import { AttributeReference } from "@dashboard/attributes/utils/data";
import {
  AttributeEntityTypeEnum,
  AttributeInputTypeEnum,
  AttributeValueDetailsFragment,
  AttributeValueFragment,
  MeasurementUnitsEnum,
  PageErrorWithAttributesFragment,
  ProductErrorWithAttributesFragment,
} from "@dashboard/graphql";
import { FormsetAtomicData } from "@dashboard/hooks/useFormset";
import { AttributeValuesMetadata } from "@dashboard/products/utils/data";
import { FetchMoreProps } from "@dashboard/types";
import { RichTextGetters } from "@dashboard/utils/richText/useMultipleRichText";
import { Accordion, Box, ChervonDownIcon, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { DashboardCard } from "../Card";
import { AttributeListItem } from "./AttributeListItem";
import { AttributeRowHandlers, VariantAttributeScope } from "./types";

export interface AttributeInputData {
  inputType: AttributeInputTypeEnum;
  entityType?: AttributeEntityTypeEnum;
  unit?: MeasurementUnitsEnum | null;
  variantAttributeScope?: VariantAttributeScope;
  isRequired: boolean;
  values: AttributeValueDetailsFragment[];
  selectedValues?: AttributeValueDetailsFragment[];
  references?: AttributeReference[];
  /** Parent attribute when this attribute is a child in a grouped structure */
  parentAttribute?: { id: string; name: string; slug: string };
  /** Child attribute name when grouped under a parent; used as row label */
  childLabel?: string;
}
export type AttributeInput = FormsetAtomicData<
  AttributeInputData,
  string[],
  AttributeValuesMetadata[]
>;
export type AttributeFileInput = FormsetAtomicData<AttributeInputData, File[]>;
export interface AttributesProps extends AttributeRowHandlers {
  attributes: AttributeInput[];
  attributeValues: AttributeValueFragment[];
  fetchAttributeValues: (query: string, attributeId: string) => void;
  fetchMoreAttributeValues: FetchMoreProps;
  onAttributeSelectBlur: () => void;
  disabled: boolean;
  loading: boolean;
  errors: Array<ProductErrorWithAttributesFragment | PageErrorWithAttributesFragment>;
  title?: React.ReactNode;
  richTextGetters: RichTextGetters<string>;
}

const messages = defineMessages({
  attributesNumber: {
    id: "z0gGP+",
    defaultMessage: "{number} Attributes",
    description: "number of attributes",
  },
  header: {
    id: "3ukd9/",
    defaultMessage: "Attributes",
    description: "attributes, section header",
  },
});

type AttributeGroup = {
  parent: { id: string; name: string; slug: string } | null;
  attributes: AttributeInput[];
};

interface GroupedAttributes {
  parentGroups: AttributeGroup[];
  standaloneGroup: AttributeGroup | null;
}

function groupAttributesByParent(attributes: AttributeInput[]): GroupedAttributes {
  const parentGroups: AttributeGroup[] = [];
  let standaloneGroup: AttributeGroup | null = null;
  const keyToIndex = new Map<string, number>();

  // First pass: group by explicit parent coming from API
  for (const a of attributes) {
    if (a.data.parentAttribute) {
      const key = a.data.parentAttribute.id;
      let idx = keyToIndex.get(key);
      if (idx === undefined) {
        idx = parentGroups.length;
        keyToIndex.set(key, idx);
        parentGroups.push({
          parent: a.data.parentAttribute,
          attributes: [],
        });
      }
      parentGroups[idx].attributes.push(a);
    } else {
      if (!standaloneGroup) {
        standaloneGroup = {
          parent: null,
          attributes: [],
        };
      }
      standaloneGroup.attributes.push(a);
    }
  }

  // Heuristic fallback: if no explicit parents but we can infer a shared prefix (e.g. "Foil Color", "Foil Type")
  if (parentGroups.length === 0 && standaloneGroup?.attributes?.length) {
    const prefixMap = new Map<string, AttributeInput[]>();

    standaloneGroup.attributes.forEach(attr => {
      const label = attr.label || "";
      const words = label.split(" ");
      
      // Try to find the best prefix (1 or 2 words)
      // For "Foil Color" -> "Foil"
      // For "Spot UV For" -> "Spot UV"
      // For "Raised UV Type" -> "Raised UV"
      let prefix = "";
      
      if (words.length >= 2) {
        // Try 2-word prefix first (for cases like "Spot UV", "Raised UV")
        const twoWordPrefix = `${words[0]} ${words[1]}`;
        prefix = twoWordPrefix;
      } else if (words.length === 1) {
        prefix = words[0];
      }
      
      if (prefix && prefix.length > 1) {
        const bucket = prefixMap.get(prefix) ?? [];
        bucket.push(attr);
        prefixMap.set(prefix, bucket);
      }
    });

    // Also try single-word prefixes if two-word didn't create enough groups
    const singleWordPrefixMap = new Map<string, AttributeInput[]>();
    standaloneGroup.attributes.forEach(attr => {
      const label = attr.label || "";
      const words = label.split(" ");
      if (words.length > 0 && words[0].length > 1) {
        const bucket = singleWordPrefixMap.get(words[0]) ?? [];
        bucket.push(attr);
        singleWordPrefixMap.set(words[0], bucket);
      }
    });

    // Prefer two-word prefixes if they create valid groups
    for (const [prefix, bucket] of prefixMap.entries()) {
      if (bucket.length >= 2) {
        parentGroups.push({
          parent: { id: `pseudo-${prefix}`, name: prefix, slug: prefix.toLowerCase().replace(/\s+/g, '-') },
          attributes: bucket,
        });
      }
    }

    // If no groups from two-word prefixes, fall back to single-word
    if (parentGroups.length === 0) {
      for (const [prefix, bucket] of singleWordPrefixMap.entries()) {
        if (bucket.length >= 2) {
          parentGroups.push({
            parent: { id: `pseudo-${prefix}`, name: prefix, slug: prefix.toLowerCase() },
            attributes: bucket,
          });
        }
      }
    }

    if (parentGroups.length > 0) {
      // Remove grouped items from standalone list
      const groupedIds = new Set(parentGroups.flatMap(g => g.attributes.map(a => a.id)));
      const remaining = standaloneGroup.attributes.filter(a => !groupedIds.has(a.id));
      standaloneGroup = remaining.length ? { parent: null, attributes: remaining } : null;
    }
  }

  return { parentGroups, standaloneGroup };
}

interface GroupedAttributeBlockProps extends AttributeRowHandlers {
  parent: { id: string; name: string; slug: string };
  attributes: AttributeInput[];
  errors: Array<ProductErrorWithAttributesFragment | PageErrorWithAttributesFragment>;
  attributeValues: AttributeValueFragment[];
  disabled: boolean;
  loading: boolean;
  onAttributeSelectBlur: () => void;
  richTextGetters: RichTextGetters<string>;
  }

const GroupedAttributeBlock = ({
  parent,
  attributes,
  errors,
  attributeValues,
  onAttributeSelectBlur,
  richTextGetters,
  ...handlers
}: GroupedAttributeBlockProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Box marginTop={4}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        paddingX={3}
        paddingY={2}
        borderRadius={2}
        borderStyle="solid"
        borderWidth={1}
        borderColor="default1"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(o => !o)}
      >
        <Text size={4} fontWeight="bold">
          {parent.name}
        </Text>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          style={{
            transition: "transform 150ms ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ChervonDownIcon size="large" />
        </Box>
      </Box>
      {open && (
        <ul>
          {attributes.map(attribute => (
            <React.Fragment key={attribute.id}>
              <AttributeListItem
                attribute={attribute}
                errors={errors}
                attributeValues={attributeValues}
                onAttributeSelectBlur={onAttributeSelectBlur}
                richTextGetters={richTextGetters}
                displayLabel={attribute.data.childLabel ?? attribute.label}
                {...handlers}
              />
            </React.Fragment>
          ))}
        </ul>
      )}
    </Box>
  );
};

export const Attributes = ({
  attributes,
  attributeValues,
  errors,
  title,
  onAttributeSelectBlur,
  richTextGetters,
  ...props
}: AttributesProps) => {
  const intl = useIntl();
  const { parentGroups, standaloneGroup } = groupAttributesByParent(attributes);
  const [parentSectionOpen, setParentSectionOpen] = useState(false);
  const [standaloneOpen, setStandaloneOpen] = useState(false);

  return (
    <DashboardCard paddingTop={6}>
      <DashboardCard.Content>
        <Box display="flex" flexDirection="column" gap={1}>
          <Accordion defaultValue="attributes-accordion">
            <Accordion.Item value="attributes-accordion">
              <Accordion.Trigger
                data-testid="attributes-expand"
                flexWrap="wrap"
                alignItems="flex-start"
              >
                <Box display="flex" flexDirection="column" gap={2}>
                  <Text size={5} fontWeight="bold">
                    {title || intl.formatMessage(messages.header)}
                  </Text>
                  <Text size={2} color="default2">
                    <FormattedMessage
                      {...messages.attributesNumber}
                      values={{
                        number: attributes.length,
                      }}
                    />
                  </Text>
                </Box>
                <Accordion.TriggerButton dataTestId="expand-icon" />
              </Accordion.Trigger>
              <Accordion.Content>
                {attributes.length > 0 && (
                  <>
                    {/* Render parent groups first */}
                    {parentGroups.length > 0 && (
                      <>
                        <Box
                          marginTop={4}
                          marginBottom={2}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          paddingX={3}
                          borderRadius={3}
                          borderStyle="solid"
                          borderWidth={1}
                          borderColor="default1"
                          style={{
                            cursor: "pointer",
                            height: "52px",
                          }}
                          onClick={() => setParentSectionOpen(o => !o)}
                        >
                          <Text size={3} fontWeight="bold" color="default2">
                            With Parent Attributes
                          </Text>
                          <Box
                            as="span"
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{
                              transition: "transform 150ms ease",
                              transform: parentSectionOpen ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          >
                            <ChervonDownIcon size="large" />
                          </Box>
                        </Box>
                        {parentSectionOpen &&
                          parentGroups.map(group => (
                            <GroupedAttributeBlock
                              key={group.parent!.id}
                              parent={group.parent!}
                              attributes={group.attributes}
                              errors={errors}
                              attributeValues={attributeValues}
                              onAttributeSelectBlur={onAttributeSelectBlur}
                              richTextGetters={richTextGetters}
                              {...props}
                            />
                          ))}
                      </>
                    )}
                    
                    {/* Render separator and standalone attributes if both exist */}
                    {parentGroups.length > 0 && standaloneGroup && (
                      <Box 
                        marginTop={4} 
                        marginBottom={2} 
                        borderTopWidth={1} 
                        borderColor="default1" 
                        borderTopStyle="solid"
                      />
                    )}
                    
                    {/* Render standalone attributes */}
                    {standaloneGroup && (
                      <>
                        <Box
                          marginTop={4}
                          marginBottom={2}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          paddingX={3}
                          borderRadius={3}
                          borderStyle="solid"
                          borderWidth={1}
                          borderColor="default1"
                          style={{
                            cursor: "pointer",
                            height: "52px",
                          }}
                          onClick={() => setStandaloneOpen(o => !o)}
                        >
                          <Text size={3} fontWeight="bold" color="default2">
                            Without Parent Attributes
                          </Text>
                          <Box
                            as="span"
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{
                              transition: "transform 150ms ease",
                              transform: standaloneOpen ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          >
                            <ChervonDownIcon size="large" />
                          </Box>
                        </Box>
                        {standaloneOpen && (
                          <ul>
                            {standaloneGroup.attributes.map(attribute => (
                              <React.Fragment key={attribute.id}>
                                <AttributeListItem
                                  attribute={attribute}
                                  errors={errors}
                                  attributeValues={attributeValues}
                                  onAttributeSelectBlur={onAttributeSelectBlur}
                                  richTextGetters={richTextGetters}
                                  {...props}
                                />
                              </React.Fragment>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </>
                )}
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        </Box>
      </DashboardCard.Content>
    </DashboardCard>
  );
};